import { useState, useEffect, useCallback, Fragment } from "react";
import { colors } from "../theme";
import { IdleGroveLogo } from "../components/IdleGroveLogo";
import { ConcordiumLogo } from "../components/ConcordiumLogo";

interface SlideProps {
  isDark: boolean;
}

function Slide({
  isDark,
  children,
}: SlideProps & { children: React.ReactNode }) {
  return (
    <div
      className="slide w-[1920px] h-[1080px] p-16 flex flex-col relative overflow-hidden"
      style={{
        backgroundColor: isDark ? colors.base03 : colors.base3,
        color: isDark ? colors.base0 : colors.base00,
        fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
        pageBreakAfter: "always",
        pageBreakInside: "avoid",
      }}
    >
      {children}
    </div>
  );
}

function SlideHeader({
  isDark,
  subtitle,
}: {
  isDark: boolean;
  subtitle: string;
}) {
  const accentColor = isDark ? colors.cyan : colors.blue;
  return (
    <div className="flex items-center justify-between mb-10 min-h-[70px]">
      <div className="flex items-center gap-4">
        <IdleGroveLogo size={72} isDark={isDark} />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Idle Grove</h1>
          <p
            className="text-lg"
            style={{ color: isDark ? colors.base01 : colors.base1 }}
          >
            {subtitle}
          </p>
        </div>
      </div>
      <div
        className="px-5 py-2.5 rounded-full text-base font-medium"
        style={{
          backgroundColor: `${accentColor}20`,
          color: accentColor,
        }}
      >
        idlegrove.com
      </div>
    </div>
  );
}

function SlideFooter({
  isDark,
  left,
  right,
}: {
  isDark: boolean;
  left: string;
  right: string;
}) {
  const accentColor = isDark ? colors.cyan : colors.blue;
  return (
    <div
      className="mt-auto pt-6 flex items-center justify-between"
      style={{ borderColor: isDark ? colors.base02 : colors.base2 }}
    >
      <p style={{ color: isDark ? colors.base01 : colors.base1 }}>{left}</p>
      <p className="font-medium" style={{ color: accentColor }}>
        {right}
      </p>
    </div>
  );
}

function PricingChart({ isDark }: { isDark: boolean }) {
  const accentColor = isDark ? colors.cyan : colors.blue;
  const tradColor = isDark ? colors.base01 : colors.base1;

  return (
    <div className="flex-1 flex items-end gap-3 pb-4">
      {[100, 82, 65, 50, 38, 28].map((height, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-2">
          <div className="w-full flex gap-1.5" style={{ height: "180px" }}>
            <div
              className="flex-1 rounded-t-lg"
              style={{
                height: `${height}%`,
                backgroundColor: accentColor,
                marginTop: "auto",
              }}
            />
            <div
              className="flex-1 rounded-t-lg"
              style={{
                height: "100%",
                backgroundColor: tradColor,
                opacity: 0.3,
              }}
            />
          </div>
          <span
            className="text-sm font-medium"
            style={{ color: isDark ? colors.base01 : colors.base1 }}
          >
            Y{i + 1}
          </span>
        </div>
      ))}
    </div>
  );
}

function StatBlock({
  value,
  label,
  isDark,
  color,
}: {
  value: string;
  label: string;
  isDark: boolean;
  color?: string;
}) {
  const accentColor = color || (isDark ? colors.cyan : colors.blue);
  return (
    <div className="text-center">
      <div className="text-4xl font-bold mb-1" style={{ color: accentColor }}>
        {value}
      </div>
      <div
        className="text-base font-medium"
        style={{ color: isDark ? colors.base01 : colors.base1 }}
      >
        {label}
      </div>
    </div>
  );
}

// =============================================================================
// VISUAL COMPONENTS
// =============================================================================

function NodeGrid({
  rows = 3,
  cols = 24,
  activeIndices = [7, 18, 35, 42, 53, 66],
  isDark = true,
}: {
  rows?: number;
  cols?: number;
  activeIndices?: number[];
  isDark?: boolean;
}) {
  const activeColor = isDark ? colors.cyan : colors.blue;
  const inactiveColor = isDark ? colors.base02 : colors.base2;

  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
    >
      {Array.from({ length: rows * cols }, (_, i) => {
        const isActive = activeIndices.includes(i);
        return (
          <div
            key={i}
            className="w-6 h-6 rounded"
            style={{
              backgroundColor: isActive ? activeColor : inactiveColor,
              boxShadow: isActive ? `0 0 16px ${activeColor}50` : "none",
            }}
          />
        );
      })}
    </div>
  );
}

function NodeGridBackground({
  activeIndices,
  isDark,
}: {
  activeIndices: number[];
  isDark: boolean;
}) {
  const bgColor = isDark ? colors.base03 : colors.base3;

  return (
    <div className="absolute top-0 left-0 right-0 h-42 overflow-hidden opacity-35 pointer-events-none">
      <div className="absolute inset-0 flex items-center justify-end pr-8">
        <NodeGrid
          rows={3}
          cols={36}
          activeIndices={activeIndices}
          isDark={isDark}
        />
      </div>
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, transparent 0%, ${bgColor} 100%)`,
        }}
      />
    </div>
  );
}

function DataFlowDiagram() {
  const steps = [
    { label: "Ingest", icon: "↓", color: colors.blue },
    { label: "Index & Classify", icon: "◎", color: colors.magenta, sub: "ML" },
    { label: "Store", icon: "⏻", color: colors.cyan, sub: "Power Off" },
    { label: "Search", icon: "◐", color: colors.violet, sub: "Always On" },
    {
      label: "Retrieve",
      icon: "↗",
      color: colors.green,
      sub: "Wake on Demand",
    },
  ];

  return (
    <div className="flex items-start justify-center gap-5">
      {steps.map((step, i) => (
        <Fragment key={step.label}>
          <div className="flex flex-col items-center">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-lg mb-2"
              style={{ backgroundColor: `${step.color}25`, color: step.color }}
            >
              {step.icon}
            </div>
            <span className="text-xs font-semibold whitespace-nowrap">
              {step.label}
            </span>
            {step.sub && (
              <span
                className="text-xs whitespace-nowrap"
                style={{ color: colors.base01 }}
              >
                {step.sub}
              </span>
            )}
          </div>
          {i < steps.length - 1 && (
            <span
              className="text-3xl font-bold mt-2"
              style={{ color: colors.base01 }}
            >
              →
            </span>
          )}
        </Fragment>
      ))}
    </div>
  );
}

// =============================================================================
// SLIDE 1: BUSINESS CASE (Light Mode)
// =============================================================================
function BusinessSlide() {
  const isDark = false;
  const accentColor = colors.blue;

  const verticals = [
    {
      name: "Healthcare",
      desc: "HIPAA-compliant records with transcribed notes",
    },
    {
      name: "Media & Entertainment",
      desc: "Raw footage, auto-indexed and findable",
    },
    {
      name: "Legal & Finance",
      desc: "Compliance records, call transcripts included",
    },
    {
      name: "Research & Government",
      desc: "Scientific data and public records",
    },
    { name: "Gaming & Esports", desc: "Build archives, player data, and VODs" },
    { name: "MSPs & Hosting", desc: "White-label archive tiers for clients" },
    {
      name: "Indigenous Orgs",
      desc: "Sovereign data, oral history preservation",
    },
  ];

  const valueProps = [
    {
      icon: "↓",
      title: "Costs decrease over time",
      desc: "Old data shouldn't cost the same as yesterday's",
    },
    {
      icon: "↗",
      title: "No egress surprises",
      desc: "Predictable pricing when you need data back",
    },
    {
      icon: "◎",
      title: "Intelligent by default",
      desc: "Indexed, classified, and searchable from day one",
    },
    {
      icon: "♻",
      title: "Green energy ready",
      desc: "Off-grid capable; on-prem hardware can connect to your renewables",
    },
    {
      icon: "⬡",
      title: "Lock in your rates",
      desc: "Long-term contracts available to protect against price volatility",
    },
  ];

  return (
    <Slide isDark={isDark}>
      <NodeGridBackground
        activeIndices={[5, 12, 19, 31, 38, 52, 58, 67]}
        isDark={isDark}
      />

      <SlideHeader
        isDark={isDark}
        subtitle="Ultra-efficient cold storage infrastructure"
      />

      <div className="flex-1 grid grid-cols-2 gap-14">
        {/* Left column */}
        <div className="flex flex-col">
          <h2 className="text-4xl font-bold leading-tight mb-2">
            Your data gets cheaper the longer it sticks around
          </h2>
          <p
            className="text-lg mb-6 leading-relaxed"
            style={{ color: colors.base1 }}
          >
            Most data just sits there. Backups, compliance records, old
            projects—rarely accessed but can't be deleted. Traditional cloud
            charges the same price forever. We think that's backwards.
          </p>

          {/* Value props */}
          <div className="space-y-6 mt-4 mb-8">
            {valueProps.map((v, i) => (
              <div key={i} className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0"
                  style={{
                    backgroundColor: `${accentColor}15`,
                    color: accentColor,
                  }}
                >
                  {v.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-lg">{v.title}</h4>
                  <p className="text-base" style={{ color: colors.base1 }}>
                    {v.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div
            className="grid grid-cols-3 gap-6 p-6 rounded-2xl mt-auto"
            style={{ backgroundColor: colors.base2 }}
          >
            <StatBlock value="75%" label="Energy Savings" isDark={isDark} />
            <StatBlock value="10+ PB" label="Per Rack" isDark={isDark} />
            <StatBlock value="0" label="Egress Traps" isDark={isDark} />
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col">
          {/* Pricing chart */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Pricing over time</h3>
              <div className="flex items-center gap-5 text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: accentColor }}
                  />
                  <span>Idle Grove</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded opacity-30"
                    style={{ backgroundColor: colors.base1 }}
                  />
                  <span style={{ color: colors.base1 }}>Traditional</span>
                </div>
              </div>
            </div>
            <PricingChart isDark={isDark} />
          </div>

          {/* Target verticals */}
          <div
            className="flex-1 rounded-2xl p-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold text-lg mb-4">
              Built for long-term archives
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {verticals.map((v, i) => (
                <div
                  key={i}
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: colors.base3 }}
                >
                  <h4 className="font-semibold mb-1">{v.name}</h4>
                  <p className="text-sm" style={{ color: colors.base1 }}>
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="You save. We save. The planet saves."
        right="Learn more → idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// SLIDE 2: ARCHITECTURE & ROADMAP (Dark Mode)
// =============================================================================
function TechSlide() {
  const isDark = true;
  const accentColor = colors.cyan;

  const architectureFeatures = [
    {
      icon: "⏻",
      title: "True Power-Off",
      desc: "Drives electrically disconnected when idle. Not sleep mode—actually off.",
      color: colors.cyan,
    },
    {
      icon: "◐",
      title: "Smart Scheduling",
      desc: "Batches requests, learns access patterns, wakes only what's needed.",
      color: colors.blue,
    },
    {
      icon: "◎",
      title: "Semantic Search",
      desc: "Content indexed on ingest. Image recognition, audio transcription, auto-classification.",
      color: colors.magenta,
    },
    {
      icon: "◈",
      title: "Distributed Resilience",
      desc: "Erasure coding across drives. Hardware fails; your data doesn't.",
      color: colors.violet,
    },
  ];

  const roadmap = [
    {
      name: "Extra Durability",
      desc: "Enhanced redundancy with encrypted parity to partner sites",
    },
    { name: "Priority Access", desc: "Faster wake times on demand" },
    { name: "Private Inference", desc: "PII/HIPAA-compliant ML on your data" },
    {
      name: "Data Scrubs & Reports",
      desc: "Integrity verification and compliance reporting",
    },
  ];

  const specs = [
    { label: "Per Box", storage: "70 TB", power: "25 W" },
    { label: "Per Shelf", storage: "1.47 PB", power: "600 W" },
    { label: "Per Rack", storage: "10 PB", power: "4.5 kW" },
  ];

  return (
    <Slide isDark={isDark}>
      <NodeGridBackground
        activeIndices={[8, 15, 23, 34, 41, 55, 62, 68]}
        isDark={isDark}
      />

      <SlideHeader isDark={isDark} subtitle="Architecture & Roadmap" />

      <div className="flex-1 grid grid-cols-5 gap-10">
        {/* Left column - Architecture (3 cols) */}
        <div className="col-span-3 flex flex-col">
          <h2 className="text-4xl font-bold leading-tight mb-2">
            Where off means truly off
          </h2>
          <p className="text-lg mb-6" style={{ color: colors.base01 }}>
            Purpose-built infrastructure where dormant data lives on hardware
            that's physically powered down.
          </p>

          {/* Architecture grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {architectureFeatures.map((f, i) => (
              <div
                key={i}
                className="p-5 rounded-xl"
                style={{ backgroundColor: colors.base02 }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center text-xl shrink-0"
                    style={{ backgroundColor: `${f.color}20`, color: f.color }}
                  >
                    {f.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">{f.title}</h4>
                    <p className="text-sm" style={{ color: colors.base01 }}>
                      {f.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Data lifecycle */}
          <div
            className="rounded-xl p-6 mb-4"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold mb-4">Data Lifecycle</h3>
            <DataFlowDiagram />
          </div>

          {/* Specs table */}
          <div
            className="rounded-xl p-5"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold mb-4">Hardware Specifications</h3>
            <div className="grid grid-cols-3 gap-4">
              {specs.map((s, i) => (
                <div key={i} className="text-center">
                  <div
                    className="text-sm mb-2"
                    style={{ color: colors.base01 }}
                  >
                    {s.label}
                  </div>
                  <div
                    className="text-2xl font-bold"
                    style={{ color: accentColor }}
                  >
                    {s.storage}
                  </div>
                  <div className="text-sm" style={{ color: colors.base01 }}>
                    @ {s.power} max
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column - Roadmap (2 cols) */}
        <div className="col-span-2 flex flex-col">
          <div
            className="rounded-xl p-6 flex-1"
            style={{ backgroundColor: colors.base02 }}
          >
            <div className="flex items-center gap-2 mb-5">
              <span
                className="text-xs uppercase tracking-widest px-2 py-1 rounded-full"
                style={{
                  backgroundColor: `${colors.magenta}20`,
                  color: colors.magenta,
                }}
              >
                Coming Soon
              </span>
              <h3 className="font-semibold text-lg">Feature Roadmap</h3>
            </div>

            <div className="space-y-4">
              {roadmap.map((r, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: colors.base03 }}
                >
                  <h4 className="font-semibold mb-1">{r.name}</h4>
                  <p className="text-sm" style={{ color: colors.base01 }}>
                    {r.desc}
                  </p>
                </div>
              ))}
            </div>

            <div
              className="mt-6 p-4 rounded-lg border-2 border-dashed"
              style={{ borderColor: colors.base01 }}
            >
              <h4 className="font-semibold mb-1">Two paths to market</h4>
              <p className="text-sm" style={{ color: colors.base01 }}>
                <strong style={{ color: colors.base0 }}>Cloud storage</strong> —
                S3-compatible archive service
              </p>
              <p className="text-sm" style={{ color: colors.base01 }}>
                <strong style={{ color: colors.base0 }}>On-prem / colo</strong>{" "}
                — Hardware-as-a-service for regulated data
              </p>
            </div>
          </div>

          {/* Validation */}
          <div
            className="rounded-xl p-5 mt-4"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold mb-3">Validated Architecture</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <span style={{ color: colors.green }}>✓</span>
                <span>Core SDS software on ARM</span>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: colors.green }}>✓</span>
                <span>Power budgets lab tested</span>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: colors.green }}>✓</span>
                <span>Initial custom hardware stable</span>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: colors.green }}>✓</span>
                <span>Flexible erasure coding working</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="The hard parts are validated. Now we're building toward production."
        right="Learn more → idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// SLIDE 3: AUDIT & COMPLIANCE (Light Mode) - Concordium Grant Pitch
// =============================================================================
function AuditSlide() {
  const isDark = false;
  const accentColor = colors.blue;

  const sealingFlow = [
    { label: "Edge Capture", icon: "◉", color: colors.blue, sub: "Every Node" },
    { label: "Bundle", icon: "▦", color: colors.violet, sub: "Logs + Metrics" },
    { label: "Hash", icon: "⬡", color: colors.cyan, sub: "Cryptographic" },
    { label: "Seal", icon: "◈", color: colors.magenta, sub: "Concordium L1" },
    { label: "Verify", icon: "✓", color: colors.green, sub: "Tamper-Proof" },
  ];

  const dataTypes = [
    {
      name: "Raw Access Logs",
      desc: "User ID, file ID, timestamp, action type",
    },
    { name: "Columnar Metrics", desc: "Aggregated access patterns per window" },
    { name: "Timeseries Data", desc: "Historical trends, usage analytics" },
  ];

  const benefits = [
    {
      icon: "🔒",
      title: "Immutable proof",
      desc: "L1 finality means audit trails can't be altered retroactively",
    },
    {
      icon: "⚡",
      title: "Verified alerts",
      desc: "Anomaly detection with cryptographic proof—not just logs, but evidence",
    },
    {
      icon: "🤝",
      title: "Third-party verifiable",
      desc: "Auditors verify seals without accessing underlying customer data",
    },
  ];

  return (
    <Slide isDark={isDark}>
      <NodeGridBackground
        activeIndices={[3, 17, 28, 44, 51, 65, 71, 82]}
        isDark={isDark}
      />

      {/* Custom header with both logos */}
      <div className="flex items-center justify-between mb-10 min-h-[70px]">
        <div className="flex items-center gap-6">
          <IdleGroveLogo size={72} isDark={isDark} />
          <span className="text-2xl font-light" style={{ color: colors.base1 }}>
            ×
          </span>
          <ConcordiumLogo height={32} isDark={isDark} />
        </div>
        <div
          className="px-5 py-2.5 rounded-full text-base font-medium"
          style={{
            backgroundColor: `${colors.blue}20`,
            color: colors.blue,
          }}
        >
          Integration Proposal
        </div>
      </div>

      <div className="flex-1 grid grid-cols-2 gap-14">
        {/* Left column */}
        <div className="flex flex-col">
          <h2 className="text-4xl font-bold leading-tight mb-2">
            Tamper-proof audit trails, sealed on-chain
          </h2>
          <p
            className="text-lg mb-6 leading-relaxed"
            style={{ color: colors.base1 }}
          >
            Every archive access generates verifiable proof. Edge nodes capture
            and hash logs locally, then seal a zero-knowledge proof to
            Concordium—audit integrity without emitting sensitive data to L1.
          </p>

          {/* Benefits */}
          <div className="space-y-4 mb-8">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0"
                  style={{
                    backgroundColor: `${accentColor}15`,
                    color: accentColor,
                  }}
                >
                  {b.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-lg">{b.title}</h4>
                  <p className="text-base" style={{ color: colors.base1 }}>
                    {b.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Audit UI mockup */}
          <div className="mt-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Audit Log</h3>
              <div
                className="flex items-center gap-1.5 text-sm"
                style={{ color: colors.base1 }}
              >
                <span>Seal contains:</span>
                <span
                  className="px-2 py-0.5 rounded"
                  style={{ backgroundColor: colors.base2 }}
                >
                  Access Logs
                </span>
                <span
                  className="px-2 py-0.5 rounded"
                  style={{ backgroundColor: colors.base2 }}
                >
                  Metrics
                </span>
                <span
                  className="px-2 py-0.5 rounded"
                  style={{ backgroundColor: colors.base2 }}
                >
                  Timeseries
                </span>
              </div>
            </div>
            <div
              className="rounded-xl overflow-hidden text-sm"
              style={{ border: `1px solid ${colors.base2}` }}
            >
              {/* Table header */}
              <div
                className="grid grid-cols-12 gap-2 px-3 py-2 font-semibold"
                style={{ backgroundColor: colors.base2 }}
              >
                <div className="col-span-2">Time</div>
                <div className="col-span-2">Edge</div>
                <div className="col-span-2">Window</div>
                <div className="col-span-1 text-right">Recs</div>
                <div className="col-span-1 text-right">Size</div>
                <div className="col-span-2">Seal</div>
                <div className="col-span-2 text-right">Status</div>
              </div>
              {/* Table rows */}
              {[
                {
                  time: "14:32:01",
                  edge: "edge-ca-w1",
                  window: "12:00-18:00",
                  records: "2,847",
                  size: "4.2 MB",
                  seal: "7f3a9e2b",
                  status: "sealed",
                },
                {
                  time: "14:31:58",
                  edge: "edge-us-w1",
                  window: "12:00-18:00",
                  records: "1,923",
                  size: "2.8 MB",
                  seal: "2b8c4e1f",
                  status: "sealed",
                },
                {
                  time: "14:31:42",
                  edge: "edge-eu-c1",
                  window: "12:00-18:00",
                  records: "3,104",
                  size: "5.1 MB",
                  seal: "9d1f6a3c",
                  status: "sealed",
                },
                {
                  time: "14:31:39",
                  edge: "edge-ap-s1",
                  window: "12:00-18:00",
                  records: "1,456",
                  size: "2.1 MB",
                  seal: "—",
                  status: "sealing",
                },
              ].map((row, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-2 px-3 py-2 items-center"
                  style={{
                    backgroundColor:
                      i % 2 === 0 ? colors.base3 : `${colors.base2}50`,
                  }}
                >
                  <div className="col-span-2" style={{ color: colors.base1 }}>
                    {row.time}
                  </div>
                  <div className="col-span-2 font-medium">{row.edge}</div>
                  <div className="col-span-2" style={{ color: colors.base1 }}>
                    {row.window}
                  </div>
                  <div
                    className="col-span-1 text-right"
                    style={{ color: colors.base1 }}
                  >
                    {row.records}
                  </div>
                  <div
                    className="col-span-1 text-right"
                    style={{ color: colors.base1 }}
                  >
                    {row.size}
                  </div>
                  <code className="col-span-2" style={{ color: colors.base1 }}>
                    {row.seal}...
                  </code>
                  <div className="col-span-2 flex justify-end">
                    {row.status === "sealed" ? (
                      <span
                        className="px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: `${colors.green}20`,
                          color: colors.green,
                        }}
                      >
                        ✓ Sealed
                      </span>
                    ) : (
                      <span
                        className="px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: `${colors.yellow}20`,
                          color: colors.yellow,
                        }}
                      >
                        ◌ Sealing
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Future vision */}
          <div className="flex items-center gap-3 mt-8">
            <div
              className="text-xs px-2.5 py-1 rounded-full shrink-0"
              style={{
                backgroundColor: `${colors.violet}15`,
                color: colors.violet,
              }}
            >
              Future
            </div>
            <p className="text-sm" style={{ color: colors.base1 }}>
              Archival stablecoin for prepaid storage, opening web3 capacity
              markets.
            </p>
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col">
          {/* Sealing flow */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold text-lg mb-6">Sealing Flow</h3>
            <div className="flex items-start justify-between">
              {sealingFlow.map((step, i) => (
                <Fragment key={step.label}>
                  <div className="flex flex-col items-center">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center text-xl mb-2"
                      style={{
                        backgroundColor: `${step.color}20`,
                        color: step.color,
                      }}
                    >
                      {step.icon}
                    </div>
                    <span className="text-sm font-semibold whitespace-nowrap">
                      {step.label}
                    </span>
                    <span className="text-xs" style={{ color: colors.base1 }}>
                      {step.sub}
                    </span>
                  </div>
                  {i < sealingFlow.length - 1 && (
                    <span
                      className="text-2xl font-bold mt-3"
                      style={{ color: colors.base1 }}
                    >
                      →
                    </span>
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          {/* How it works */}
          <div
            className="flex-1 rounded-2xl p-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold text-lg mb-4">
              How verification works
            </h3>
            <div className="space-y-4 text-sm">
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">1. Access window closes</h4>
                <p style={{ color: colors.base1 }}>
                  Edge node bundles logs into tenant-isolated buckets—never
                  shared or aggregated across customers
                </p>
              </div>
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">
                  2. ZK proof sealed on-chain
                </h4>
                <p style={{ color: colors.base1 }}>
                  Only the proof hits L1—no sensitive data emitted. Concordium
                  returns a seal with finality.
                </p>
              </div>
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">3. Audit trail verified</h4>
                <p style={{ color: colors.base1 }}>
                  If local records match the on-chain seal, the audit log is
                  proven untampered
                </p>
              </div>
            </div>
          </div>

          {/* Concordium callout */}
          <div
            className="mt-6 p-4 rounded-xl flex items-center gap-4"
            style={{
              backgroundColor: `${colors.magenta}10`,
              border: `1px solid ${colors.magenta}30`,
            }}
          >
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-xl shrink-0"
              style={{
                backgroundColor: `${colors.magenta}20`,
                color: colors.magenta,
              }}
            >
              ◈
            </div>
            <div>
              <h4 className="font-semibold">Why Concordium?</h4>
              <p className="text-sm" style={{ color: colors.base1 }}>
                From years of chain infra development experience, we wanted
                something no-nonsense. Native ZKPs, stable fees, fast
                finality—infrastructure, not speculation.
              </p>
            </div>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="Idle Grove Edge: Bundle → Seal → Ship"
        right="Grant proposal  •  idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// SLIDE 4: HARDWARE ARCHITECTURE (Dark Mode)
// =============================================================================
function HardwareSlide() {
  const isDark = true;
  const accentColor = colors.cyan;

  const nodeStack = [
    {
      label: "RK3588 SoM",
      desc: "ARM compute, 2.5GbE, PCIe",
      color: colors.blue,
    },
    {
      label: "ASM1166",
      desc: "6-port PCIe→SATA controller",
      color: colors.violet,
    },
    {
      label: "ESP32-C6 MCU",
      desc: "Power control, telemetry, wireless backhaul",
      color: colors.cyan,
    },
    {
      label: "5× 14TB Drives",
      desc: "Individually switched, hot-pluggable",
      color: colors.green,
    },
  ];

  const differentiators = [
    {
      icon: "⏻",
      title: "Physical power isolation",
      desc: "MCU gets scheduled and coordinates to disconnect drives via SATA sense pins—actually off, not spin-down. Deep sleep shuts down the SoM too when we anticipate it won't be needed. Custom Linux boots in seconds.",
      color: colors.cyan,
    },
    {
      icon: "📡",
      title: "Out-of-band telemetry",
      desc: "MCU monitors per-drive power, per-node power, and board thermals—reported over ESP-NOW mesh even when the SoM is asleep or main network is down. Realtime DCIM intelligence for prod management and debugging. Battery-backed to survive PoE loss.",
      color: colors.magenta,
    },
    {
      icon: "⚡",
      title: "PoE+ everything",
      desc: "Single cable per node. No BMC, no IPMI, no PDUs. MCU blinks a light on failure; untrained staff can pull the shelf and swap a node in minutes.",
      color: colors.yellow,
    },
    {
      icon: "🔧",
      title: "Built in-house",
      desc: "Custom PCB, firmware, software, and mechanical design—we're building expertise and IP, not outsourcing. Shelf-level cooling adjusts in realtime from MCU telemetry. Nodes have open airflow channels while staying shielded and vibration-isolated.",
      color: colors.green,
    },
  ];

  return (
    <Slide isDark={isDark}>
      <NodeGridBackground
        activeIndices={[2, 9, 14, 27, 33, 48, 55, 61, 70]}
        isDark={isDark}
      />

      <SlideHeader isDark={isDark} subtitle="Hardware Architecture" />

      <div className="flex-1 grid grid-cols-2 gap-14">
        {/* Left column - Node diagram */}
        <div className="flex flex-col">
          <h2 className="text-4xl font-bold leading-tight mb-2">
            Weird enough to be a moat
          </h2>
          <p className="text-lg mb-6" style={{ color: colors.base01 }}>
            Custom hardware that incumbents won't build because it cannibalizes
            their margins, and most startups can't because it requires going
            deep across too many domains at once.
          </p>

          {/* Node architecture diagram */}
          <div
            className="rounded-xl p-6 mb-6"
            style={{ backgroundColor: colors.base02 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Node Architecture</h3>
              <div className="flex items-center gap-3 text-sm">
                <span style={{ color: colors.base01 }}>70TB</span>
                <span style={{ color: colors.base01 }}>•</span>
                <span style={{ color: accentColor }}>25W max</span>
                <span style={{ color: colors.base01 }}>•</span>
                <span style={{ color: colors.green }}>4W idle</span>
                <span style={{ color: colors.base01 }}>•</span>
                <span style={{ color: colors.violet }}>~0W deep</span>
              </div>
            </div>

            {/* Visual stack */}
            <div className="space-y-3">
              {/* PoE+ input */}
              <div className="flex items-center gap-3">
                <div
                  className="px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: `${colors.yellow}20`,
                    color: colors.yellow,
                  }}
                >
                  PoE+ In
                </div>
                <div
                  className="flex-1 h-px"
                  style={{ backgroundColor: colors.base01 }}
                />
                <span className="text-xs" style={{ color: colors.base01 }}>
                  Power + Data
                </span>
              </div>

              {/* Component stack */}
              {nodeStack.map((item, i) => (
                <div key={i} className="flex items-stretch gap-3">
                  <div
                    className="w-1 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <div
                    className="flex-1 p-3 rounded-lg"
                    style={{ backgroundColor: colors.base03 }}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className="font-semibold"
                        style={{ color: item.color }}
                      >
                        {item.label}
                      </span>
                      <span
                        className="text-sm"
                        style={{ color: colors.base01 }}
                      >
                        {item.desc}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Connection annotations */}
              <div
                className="flex items-center gap-6 pt-2 text-xs"
                style={{ color: colors.base01 }}
              >
                <span>↕ SPI control bus</span>
                <span>↕ GPIO sense pins</span>
                <span>📡 ESP-NOW mesh → shelf hub</span>
              </div>
            </div>
          </div>

          {/* Shelf scale */}
          <div
            className="rounded-xl p-5"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold mb-4">Scale</h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                {
                  label: "Node",
                  storage: "70 TB",
                  power: "25W max",
                  idle: "4W idle",
                  deep: "~0W deep",
                },
                {
                  label: "Shelf (21 nodes)",
                  storage: "1.47 PB",
                  power: "600W max",
                  idle: "<120W idle",
                  deep: null,
                },
                {
                  label: "Rack (7 shelves)",
                  storage: "10 PB",
                  power: "4.5kW max",
                  idle: "<700W idle",
                  deep: null,
                },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div
                    className="text-sm mb-2"
                    style={{ color: colors.base01 }}
                  >
                    {s.label}
                  </div>
                  <div
                    className="text-2xl font-bold"
                    style={{ color: accentColor }}
                  >
                    {s.storage}
                  </div>
                  <div className="text-sm" style={{ color: colors.base01 }}>
                    {s.power} · {s.idle}
                    {s.deep ? ` · ${s.deep}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column - Differentiators */}
        <div className="flex flex-col">
          <div
            className="rounded-xl p-6 flex-1"
            style={{ backgroundColor: colors.base02 }}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-lg">What makes it different</h3>
              <span
                className="text-xs px-2.5 py-1 rounded-full"
                style={{
                  backgroundColor: `${colors.yellow}20`,
                  color: colors.yellow,
                }}
              >
                ⚠ technical details below
              </span>
            </div>
            <div className="space-y-4">
              {differentiators.map((d, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: colors.base03 }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0"
                      style={{
                        backgroundColor: `${d.color}20`,
                        color: d.color,
                      }}
                    >
                      {d.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{d.title}</h4>
                      <p className="text-sm" style={{ color: colors.base01 }}>
                        {d.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* The moat callout */}
          <div
            className="mt-6 p-5 rounded-xl"
            style={{
              backgroundColor: `${colors.orange}10`,
              border: `1px solid ${colors.orange}30`,
            }}
          >
            <h4 className="font-semibold mb-2" style={{ color: colors.orange }}>
              The moat is the weirdness
            </h4>
            <p className="text-sm" style={{ color: colors.base01 }}>
              ARM SoMs, PoE power, MCU-controlled drive switching, wireless
              backhaul, battery backup—none of this is off-the-shelf. It's too
              strange for incumbents to copy and too deep for most startups to
              attempt. Over a decade building storage and cloud infra at scale,
              and this is the thing we'd build next.
            </p>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="Simple. Bulletproof. Ours."
        right="idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// SLIDE 5: INVESTMENT CASE (Light Mode)
// =============================================================================
function InvestorSlide() {
  const isDark = false;
  const accentColor = colors.blue;

  const alignmentPoints = [
    {
      icon: "◉",
      title: "Storage sustains itself",
      desc: "Archive storage isn't a loss leader here—it's baseline SaaS revenue that sustains us from day one. When customers are happy, they spend more on features they actually want. That's how we grow: by being worth it, not by trapping anyone.",
      color: colors.blue,
    },
    {
      icon: "⚖",
      title: "We understand the economics",
      desc: "We've operated clouds at scale. We know CAC, COGS, refresh cycles, failure rates, and how to capitalize hardware. Our pricing model aligns with reality—costs go down as data ages because that's what it actually costs us to store it.",
      color: colors.green,
    },
    {
      icon: "∞",
      title: "Value through substance and scale",
      desc: "Our value is proprietary hardware and software IP, plus long-term customers who trust us with their archives—real assets that compound, not vanity metrics. No legacy overhead means we can undercut incumbents while staying lean. Efficiency is the product.",
      color: colors.violet,
    },
    {
      icon: "📦",
      title: "Unlocking priced-out markets",
      desc: "There's a whole segment of people with irreplaceable data—photographers with a lifetime of work, families with decades of memories—keeping hard drives in closets and SD cards in drawers because they think cloud is out of reach. We want to change that.",
      color: colors.orange,
    },
  ];

  return (
    <Slide isDark={isDark}>
      <NodeGridBackground
        activeIndices={[4, 11, 22, 37, 45, 58, 63, 74]}
        isDark={isDark}
      />

      <SlideHeader isDark={isDark} subtitle="Investment Case" />

      <div className="flex-1 grid grid-cols-2 gap-14">
        {/* Left column */}
        <div className="flex flex-col">
          <h2 className="text-4xl font-bold leading-tight mb-2">
            Aligned for customer love, not quick exits
          </h2>
          <p
            className="text-lg mb-6 leading-relaxed"
            style={{ color: colors.base1 }}
          >
            We're not subsidizing storage to lock you in. Happy customers with
            low costs is the whole strategy.
          </p>

          {/* Alignment points */}
          <div className="space-y-4">
            {alignmentPoints.map((p, i) => (
              <div
                key={i}
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base2 }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0"
                    style={{
                      backgroundColor: `${p.color}15`,
                      color: p.color,
                    }}
                  >
                    {p.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">{p.title}</h4>
                    <p className="text-sm" style={{ color: colors.base1 }}>
                      {p.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Go to market */}
          <div
            className="rounded-2xl p-5 mt-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold mb-3">Go to market</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">Expertise, not SEO</h4>
                <p style={{ color: colors.base1 }}>
                  Real human content from people who've built this stuff.
                </p>
              </div>
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">Gov & municipal channel</h4>
                <p style={{ color: colors.base1 }}>
                  Direct relationships in verticals that value trust and
                  longevity.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col">
          {/* Revenue alignment chart */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold text-lg mb-2">
              Customer value over time
            </h3>
            <p className="text-sm mb-4" style={{ color: colors.base1 }}>
              Old data gets cheaper as promised. Enterprise data grows at 23–61%
              CAGR
              <sup>
                <a
                  href="https://www.businesswire.com/news/home/20210324005175/en/Data-Creation-and-Replication-Will-Grow-at-a-Faster-Rate-Than-Installed-Storage-Capacity-According-to-the-IDC-Global-DataSphere-and-StorageSphere-Forecasts"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: colors.base01 }}
                >
                  [1]
                </a>
                <a
                  href="https://www.quantum.com/globalassets/documents/idc-vendor-spotlight.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: colors.blue }}
                >
                  [2]
                </a>
              </sup>
              —customers naturally generate more, especially unstructured. They
              choose services like search and retrieval when valuable. Everyone
              wins.
            </p>
            <div className="flex items-end justify-between gap-3 h-32 mb-3">
              {(() => {
                const data = [
                  { year: "Y1", storage: 85, newData: 10, services: 5 },
                  { year: "Y2", storage: 70, newData: 18, services: 15 },
                  { year: "Y3", storage: 58, newData: 25, services: 25 },
                  { year: "Y4", storage: 48, newData: 30, services: 32 },
                  { year: "Y5", storage: 40, newData: 35, services: 38 },
                  { year: "Y6", storage: 34, newData: 38, services: 45 },
                ];
                const maxTotal = Math.max(
                  ...data.map((d) => d.storage + d.newData + d.services)
                );
                return data.map((d, i) => {
                  const total = d.storage + d.newData + d.services;
                  const scale = total / maxTotal;
                  return (
                    <div
                      key={i}
                      className="flex-1 flex flex-col items-center gap-1"
                    >
                      <div
                        className="w-full flex flex-col justify-end"
                        style={{ height: "100px" }}
                      >
                        <div
                          style={{ height: `${scale * 100}%` }}
                          className="flex flex-col"
                        >
                          <div
                            className="w-full rounded-t"
                            style={{
                              flex: d.services,
                              backgroundColor: colors.cyan,
                            }}
                          />
                          <div
                            className="w-full"
                            style={{
                              flex: d.newData,
                              backgroundColor: colors.green,
                            }}
                          />
                          <div
                            className="w-full rounded-b"
                            style={{
                              flex: d.storage,
                              backgroundColor: colors.blue,
                            }}
                          />
                        </div>
                      </div>
                      <span className="text-xs" style={{ color: colors.base1 }}>
                        {d.year}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="flex items-center justify-center gap-5 text-xs">
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded"
                  style={{ backgroundColor: colors.blue }}
                />
                <span style={{ color: colors.base1 }}>Aging data</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded"
                  style={{ backgroundColor: colors.green }}
                />
                <span style={{ color: colors.base1 }}>New ingest</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded"
                  style={{ backgroundColor: colors.cyan }}
                />
                <span style={{ color: colors.base1 }}>Services</span>
              </div>
            </div>
            <div
              className="mt-4 p-3 rounded-lg text-xs"
              style={{ backgroundColor: colors.base3 }}
            >
              <span className="font-semibold">200PB simulation:</span>{" "}
              <span style={{ color: colors.base1 }}>
                Profitable across all data ages at 50%+ utilization. Upsells
                (SSO, multi-tenancy, priority retrieval) contribute ~26% of
                revenue, helping maintain margins even with fully mature
                archives.
              </span>
            </div>
            <div
              className="mt-3 text-xs"
              style={{ color: colors.base1, opacity: 0.7 }}
            >
              [1]{" "}
              <a
                href="https://www.businesswire.com/news/home/20210324005175/en/Data-Creation-and-Replication-Will-Grow-at-a-Faster-Rate-Than-Installed-Storage-Capacity-According-to-the-IDC-Global-DataSphere-and-StorageSphere-Forecasts"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.blue }}
              >
                IDC Global DataSphere Forecast
              </a>{" "}
              [2]{" "}
              <a
                href="https://www.quantum.com/globalassets/documents/idc-vendor-spotlight.pdf"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.blue }}
              >
                IDC Unstructured Data Growth
              </a>
            </div>
          </div>

          {/* Deployment models */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold text-lg mb-4">
              Deployment flexibility
            </h3>
            <div className="space-y-3 text-sm">
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">Our facilities</h4>
                <p style={{ color: colors.base1 }}>
                  Direct service from our infrastructure. We handle everything.
                </p>
              </div>
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">On-prem / colo lease</h4>
                <p style={{ color: colors.base1 }}>
                  Customers who require physical hardware on-site can lease
                  racks. Same software, same support, their location.
                </p>
              </div>
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">Sublet unused capacity</h4>
                <p style={{ color: colors.base1 }}>
                  On-prem customers can sublet encrypted spare capacity back to
                  our pool—offsetting their costs while we gain geographic
                  distribution.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="Sustainable by design. Honest by choice."
        right="idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// SLIDE 6: PRIVATE INFERENCE (Dark Mode) - Technical Deep Dive
// =============================================================================
function InferenceSlide() {
  const isDark = true;
  const accentColor = colors.magenta;

  const coreFeatures = [
    {
      icon: "◎",
      title: "Self-describing compute",
      desc: "Workers announce VRAM, engines, accelerators. Ryzen AI Max+, M4 Max, L40S—heterogeneous pool.",
      color: colors.cyan,
    },
    {
      icon: "⬡",
      title: "Payload-blind orchestration",
      desc: "Control plane routes metadata only. Direct mTLS to worker. SSE-C encryption—customer key in memory during execution, never persisted or logged.",
      color: colors.magenta,
    },
    {
      icon: "▦",
      title: "Any model, any engine",
      desc: "Catalog-registered, pulled on-demand. vLLM, ONNX, Triton, custom—unified API.",
      color: colors.blue,
    },
    {
      icon: "◐",
      title: "Priority scheduling",
      desc: "Interactive: immediate. Batch: set a deadline, jobs accumulate, model loads once, bulk dispatch to object storage.",
      color: colors.green,
    },
  ];

  const flowSteps = [
    {
      label: "Submit",
      desc: "Job manifest to control plane",
      color: colors.blue,
    },
    {
      label: "Match",
      desc: "Scheduler finds capable worker",
      color: colors.cyan,
    },
    { label: "Tunnel", desc: "Direct TLS to worker", color: colors.magenta },
    { label: "Execute", desc: "Encrypted in memory", color: colors.violet },
    { label: "Retrieve", desc: "Client decrypts locally", color: colors.green },
  ];

  const sessionTypes = [
    {
      name: "Pinned",
      desc: "Model stays loaded, never evicted",
      color: colors.cyan,
    },
    {
      name: "Floating",
      desc: "Best-effort affinity, may relocate",
      color: colors.blue,
    },
    {
      name: "Stateless",
      desc: "Route anywhere, no affinity",
      color: colors.base1,
    },
  ];

  return (
    <Slide isDark={isDark}>
      <NodeGridBackground
        activeIndices={[5, 14, 27, 33, 48, 56, 67, 71]}
        isDark={isDark}
      />

      <SlideHeader isDark={isDark} subtitle="Private Inference Platform" />

      <div className="flex-1 grid grid-cols-5 gap-10">
        {/* Left column - Core concepts (3 cols) */}
        <div className="col-span-3 flex flex-col">
          <h2 className="text-4xl font-bold leading-tight mb-2">
            GPU timeshare with zero-trust execution
          </h2>
          <p className="text-lg mb-6" style={{ color: colors.base01 }}>
            Heterogeneous compute that self-describes. Submit any inference
            workload—LLMs, classification, embeddings, custom models—to a shared
            pool. Work gets scheduled based on priority and capability. Data
            never touches the control plane.
          </p>

          {/* Core features grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {coreFeatures.map((f, i) => (
              <div
                key={i}
                className="p-5 rounded-xl"
                style={{ backgroundColor: colors.base02 }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center text-xl shrink-0"
                    style={{ backgroundColor: `${f.color}20`, color: f.color }}
                  >
                    {f.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">{f.title}</h4>
                    <p className="text-sm" style={{ color: colors.base01 }}>
                      {f.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Available models */}
          <div
            className="p-4 rounded-xl mb-4"
            style={{ backgroundColor: colors.base02 }}
          >
            <h4 className="font-semibold mb-2">Publicly available models</h4>
            <p className="text-sm mb-2" style={{ color: colors.base01 }}>
              Efficient nodes run frontier-class open models at full precision:
            </p>
            <div className="flex flex-wrap gap-2 text-xs mb-3">
              {[
                { name: "Llama 4", color: colors.blue },
                { name: "Llama 3.3 70B", color: colors.blue },
                { name: "Qwen 3 235B", color: colors.violet },
                { name: "Qwen 2.5 72B", color: colors.violet },
                { name: "DeepSeek R1", color: colors.cyan },
                { name: "DeepSeek V3", color: colors.cyan },
                { name: "Mixtral 8x22B", color: colors.orange },
                { name: "Mistral Small 3", color: colors.orange },
                { name: "Gemma 2", color: colors.green },
              ].map((model, i) => (
                <span
                  key={i}
                  className="px-2 py-1 rounded font-medium"
                  style={{
                    backgroundColor: `${model.color}20`,
                    color: model.color,
                    border: `1px solid ${model.color}40`,
                  }}
                >
                  {model.name}
                </span>
              ))}
            </div>
            <p className="text-sm mb-2" style={{ color: colors.base01 }}>
              Fast &amp; efficient for batch processing and embeddings:
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              {[
                { name: "Phi-4", color: colors.magenta },
                { name: "Gemma 2B", color: colors.green },
                { name: "Qwen 2.5 1.5B", color: colors.violet },
                { name: "Nomic Embed v2", color: colors.yellow },
                { name: "BGE-M3", color: colors.yellow },
                { name: "E5-Mistral", color: colors.yellow },
              ].map((model, i) => (
                <span
                  key={i}
                  className="px-2 py-1 rounded font-medium"
                  style={{
                    backgroundColor: `${model.color}20`,
                    color: model.color,
                    border: `1px solid ${model.color}40`,
                  }}
                >
                  {model.name}
                </span>
              ))}
            </div>
          </div>

          {/* Session types */}
          <div
            className="p-4 rounded-xl"
            style={{ backgroundColor: colors.base02 }}
          >
            <h4 className="font-semibold mb-3">Session affinity</h4>
            <div className="flex gap-4">
              {sessionTypes.map((s, i) => (
                <div key={i} className="flex-1">
                  <div
                    className="text-sm font-semibold mb-1"
                    style={{ color: s.color }}
                  >
                    {s.name}
                  </div>
                  <p className="text-xs" style={{ color: colors.base01 }}>
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column (2 cols) */}
        <div className="col-span-2 flex flex-col">
          {/* Zero-trust flow */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold text-lg mb-4">
              Zero-trust execution flow
            </h3>
            <div className="space-y-3">
              {flowSteps.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{
                      backgroundColor: `${step.color}30`,
                      color: step.color,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <span
                      className="font-semibold"
                      style={{ color: step.color }}
                    >
                      {step.label}
                    </span>
                    <span
                      className="text-sm ml-2"
                      style={{ color: colors.base01 }}
                    >
                      {step.desc}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div
              className="mt-4 p-3 rounded-lg text-xs"
              style={{ backgroundColor: colors.base03 }}
            >
              <span style={{ color: colors.magenta }}>SSE-C style:</span>{" "}
              <span style={{ color: colors.base01 }}>
                Customer-provided key exists only in worker memory during
                execution. Results encrypted, key discarded. Nothing logged.
              </span>
            </div>
          </div>

          {/* Runtime options */}
          <div
            className="rounded-2xl p-6 flex-1"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold text-lg mb-4">Runtime options</h3>
            <div className="space-y-4 text-sm">
              <div>
                <div
                  className="font-semibold mb-1"
                  style={{ color: colors.cyan }}
                >
                  Idle Grove cluster
                </div>
                <p style={{ color: colors.base01 }}>
                  Cloud customers get access to our dedicated inference compute.
                  Shared pool, private execution, no setup.
                </p>
              </div>
              <div>
                <div
                  className="font-semibold mb-1"
                  style={{ color: colors.green }}
                >
                  Bring your own hardware
                </div>
                <p style={{ color: colors.base01 }}>
                  Run the agent on your GPU hardware. Scheduler respects
                  provenance—run only your workloads, or lease idle capacity
                  back to the pool and evict scavengers when you need it.
                </p>
              </div>
              <div>
                <div
                  className="font-semibold mb-1"
                  style={{ color: colors.violet }}
                >
                  Vectorize your archives
                </div>
                <p style={{ color: colors.base01 }}>
                  Run embedding models against your Idle Grove data.
                  Pre-calculate embeddings, make objects ML-ready.
                </p>
              </div>
              <div>
                <div
                  className="font-semibold mb-1"
                  style={{ color: colors.orange }}
                >
                  Results
                </div>
                <p style={{ color: colors.base01 }}>
                  Streamed directly to you, or written encrypted to your Idle
                  Grove storage for later retrieval.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="Private inference on any hardware. Your models, your data, encrypted end-to-end."
        right="idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// EXPORTS - Individual slides for deck composition
// =============================================================================
export {
  BusinessSlide,
  TechSlide,
  AuditSlide,
  HardwareSlide,
  InvestorSlide,
  InferenceSlide,
};

// =============================================================================
// SLIDE PRESENTER - Reusable presentation wrapper
// =============================================================================
interface SlidePresenterProps {
  slides: React.ComponentType[];
}

export function SlidePresenter({ slides }: SlidePresenterProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scale, setScale] = useState(0.5);
  const [isMobile, setIsMobile] = useState(false);

  // Calculate scale to fit viewport
  useEffect(() => {
    const calculateScale = () => {
      const scaleX = (window.innerWidth * 0.95) / 1920;
      const scaleY = (window.innerHeight * 0.95) / 1080;
      setScale(Math.min(scaleX, scaleY, 1)); // Cap at 1x
      setIsMobile(window.innerWidth < 768 || window.innerHeight < 768);
    };
    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, []);

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => Math.min(prev + 1, slides.length - 1));
  }, []);

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev]);

  const CurrentSlideComponent = slides[currentSlide];

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ backgroundColor: "#1a1a1a" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');

        @media print {
          @page {
            size: 508mm 286mm;
            margin: 0;
          }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .slide {
            box-shadow: none !important;
            transform: none !important;
          }
        }

        @media screen {
          .print-only { display: none !important; }
          .slide {
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          }
        }

        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      `}</style>

      {/* Visible slide (screen only) */}
      <div className="no-print flex items-center justify-center flex-1 w-full relative">
        {/* Mobile full-screen tap zones with chevrons */}
        {isMobile && (
          <>
            <button
              onClick={goPrev}
              className="absolute top-0 left-0 w-1/2 h-full z-10 flex items-center justify-start pl-4"
              style={{ background: "transparent" }}
              aria-label="Previous slide"
            >
              <span
                className="text-8xl font-thin select-none"
                style={{ color: "rgba(255,255,255,0.08)" }}
              >
                ‹
              </span>
            </button>
            <button
              onClick={goNext}
              className="absolute top-0 right-0 w-1/2 h-full z-10 flex items-center justify-end pr-4"
              style={{ background: "transparent" }}
              aria-label="Next slide"
            >
              <span
                className="text-8xl font-thin select-none"
                style={{ color: "rgba(255,255,255,0.08)" }}
              >
                ›
              </span>
            </button>
            {/* Mobile page counter */}
            <div
              className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium z-20"
              style={{
                backgroundColor: "rgba(0,0,0,0.6)",
                color: "rgba(255,255,255,0.8)",
              }}
            >
              {currentSlide + 1} / {slides.length}
            </div>
          </>
        )}

        <div
          style={{
            width: 1920 * scale,
            height: 1080 * scale,
            position: "relative",
          }}
        >
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              width: 1920,
              height: 1080,
            }}
          >
            <CurrentSlideComponent />
          </div>
        </div>
      </div>

      {/* Navigation controls - fixed at bottom (desktop only) */}
      {!isMobile && (
        <div className="no-print fixed bottom-6 left-0 right-0 flex flex-col items-center z-50">
          <div
            className="flex items-center gap-6 px-6 py-3 rounded-full"
            style={{
              backgroundColor: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(8px)",
            }}
          >
            <button
              onClick={goPrev}
              disabled={currentSlide === 0}
              className="px-4 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              {"< "} Prev
            </button>
            <div className="flex items-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className="w-2.5 h-2.5 rounded-full transition-all"
                  style={{
                    backgroundColor:
                      i === currentSlide
                        ? colors.cyan
                        : "rgba(255,255,255,0.3)",
                  }}
                />
              ))}
            </div>
            <button
              onClick={goNext}
              disabled={currentSlide === slides.length - 1}
              className="px-4 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Next {" >"}
            </button>
            {/* Page counter */}
            <span className="text-white/50 text-sm px-2">
              {currentSlide + 1} / {slides.length}
            </span>
          </div>
          <div className="mt-3 text-white/30 text-xs">
            ← → to navigate · Ctrl+P to print
          </div>
        </div>
      )}

      {/* All slides for print (hidden on screen) */}
      <div className="print-only">
        {slides.map((SlideComponent, i) => (
          <SlideComponent key={i} />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// DEFAULT DECK - All brand slides
// =============================================================================
const allSlides = [
  BusinessSlide,
  TechSlide,
  InvestorSlide,
  HardwareSlide,
  AuditSlide,
  InferenceSlide,
];

export function BrandSlides() {
  return <SlidePresenter slides={allSlides} />;
}
